import { find } from 'lodash';
import { ADMIN, INTEL_CONSUMER, INTEL_PRODUCER } from '../constants/user-roles';
import { MAIN_BRAND_NAME } from '../constants/user-team-ids';
import { CHECKPOINT_DOMAIN, CYBERINT_DOMAIN } from '../constants/domains';
import { TENANT_TYPES } from '../constants/tenant-types';
import { LITMOS_CONSTS } from '../constants/index';

export const isUserInTeam = (user, teamId) => {
  if (!user || !teamId) {
    return false;
  }
  return user.team_id === teamId;
};

export const isCyberintUser = currentUser =>
  currentUser?.customer?.tenant_type
    ? currentUser.customer.tenant_type === TENANT_TYPES.CYBERINT_MANAGEMENT
    : isUserInTeam(currentUser, MAIN_BRAND_NAME);

export const isCyberintMgmtAdmin = currentUser =>
  currentUser?.customer?.tenant_type === TENANT_TYPES.CYBERINT_MANAGEMENT && isAdminUser(currentUser);

export const isCyberintRootUser = currentUser => isUserInTeam(currentUser, MAIN_BRAND_NAME);
export const isCyberintRootAdmin = currentUser => isCyberintRootUser(currentUser) && isAdminUser(currentUser);
export const isNonCyberintUser = currentUser => !isUserInTeam(currentUser, MAIN_BRAND_NAME);
export const isAdminUser = currentUser => currentUser?.role_id === ADMIN;
export const isNonAdmin = currentUser => !isAdminUser(currentUser);
export const isProducerUser = currentUser => currentUser?.role_id === INTEL_PRODUCER;
export const isConsumerUser = currentUser => currentUser?.role_id === INTEL_CONSUMER;
export const isMsspUser = currentUser => currentUser?.customer?.tenant_type === TENANT_TYPES.MSSP_MANAGEMENT;
export const isMsspAdmin = currentUser => isMsspUser(currentUser) && isAdminUser(currentUser);
export const isMsspMngAdmin = currentUser =>
  currentUser.customer.tenant_type === TENANT_TYPES.MSSP_MANAGEMENT && isAdminUser(currentUser);
export const isMultiTenantMgmtAdmin = currentUser =>
  currentUser?.customer?.tenant_type === TENANT_TYPES.MULTI_TENANT_MANAGEMENT && isAdminUser(currentUser);

export const isCyberintAdmin = currentUser => isCyberintUser(currentUser) && isAdminUser(currentUser);

const isPartnersVarManagementUser = currentUser => {
  return currentUser?.customer?.tenant_type === TENANT_TYPES.PARTNERS_VAR_MANAGEMENT;
};

export const isPartnersVarManagementAdmin = currentUser => {
  return isPartnersVarManagementUser(currentUser) && isAdminUser(currentUser);
};

export const isCyberintProducer = currentUser => isCyberintUser(currentUser) && isProducerUser(currentUser);

// Litmos-specific admin access check
export const isLitmosAdminUser = (currentUser, selectedTenant) => {
  if (!isAdminUser(currentUser)) {
    return false;
  }

  // For Cyberint admins: check the SELECTED tenant type
  if (isCyberintAdmin(currentUser)) {
    const tenantType = selectedTenant?.tenant_type;
    return LITMOS_CONSTS.LITMOS_ADMIN_SUPPORTED_TENANTS.includes(tenantType);
  }

  // For non-Cyberint admins: check the logged-in user's tenant type
  const userTenantType = currentUser?.customer?.tenant_type;
  return LITMOS_CONSTS.LITMOS_ADMIN_SUPPORTED_TENANTS.includes(userTenantType);
};

// Extracts a display-friendly username from a user identifier.
// Legacy ids look like "team/email@domain"; new ids are opaque UUIDs.
export const getUserDisplayName = userId => {
  if (typeof userId !== 'string' || !userId) return undefined;
  return userId.includes('/') ? userId.split('/').pop() : userId;
};

export const isCheckpointEmail = email => !!email?.toLowerCase().endsWith(`@${CHECKPOINT_DOMAIN}`);
export const isCheckpointUser = currentUser => isCheckpointEmail(currentUser?.username);
export const isCyberintUserByEmail = currentUser => currentUser?._id.endsWith(`@${CYBERINT_DOMAIN}`);
export const isCyberintLocalAdmin = currentUser =>
  currentUser?._id.endsWith(`@${CYBERINT_DOMAIN}`) &&
  isAdminUser(currentUser) &&
  !isUserInTeam(currentUser, MAIN_BRAND_NAME);
export const isProducerOrAdminUser = currentUser => isProducerUser(currentUser) || isAdminUser(currentUser);
export const isThreatHuntingUser = user => !!user?.threat_hunting_mode;
export const isInfinityUser = user => !!user?.infinity_user;

export function getCurrentUser(...args) {
  let userId;
  let users;

  if (args.length === 1) {
    const state = args[0];

    userId = state.currentUserIdResult.response;
    users = state.users;
  } else {
    [userId, users] = args;
  }

  return userId && users.length && find(users, { _id: userId });
}
